import { formatISO } from 'date-fns'
/**
 * @typedef {Object} Appointment
 * @property {string} availabilitySlotId - The ID of the availability slot.
 * @property {Object} appointmentType - The type of the appointment.
 * @property {string} appointmentType.name - The name of the appointment type.
 * @property {string} startTime - The start time of the appointment.
 * @property {string} providerName - The name of the provider.
 * @property {string} date - The date of the appointment.
 * @property {string} timezoneAbbr - The timezone abbreviation.
 */

/**
 * @typedef {Object} AvailabilityTime
 * @property {string} id - The ID of the availability slot.
 * @property {string} appointmentType - The type of the appointment.
 * @property {string} startTime - The start time of the appointment.
 * @property {string} provider - The name of the provider.
 */

/**
 * @typedef {Object} Availability
 * @property {string} date - The formatted date string.
 * @property {AvailabilityTime[]} times - An array of available times.
 * @property {string} timezoneAbbr - The timezone abbreviation.
 */

/**
   * Returns the availability for a given day based on Schedule Connect Availability.
   * @param {Date} day - The day for which availability needs to be checked.
   * @param {Appointment[]} availableAppointments - An array of available appointments.
   * @return {Availability} - The availability for the given day. If no availability is found, a default value is returned.
   */
export const getAvailability = (day, availableAppointments) => {
  const formattedDay = formatISO(day, { representation: 'date' })
  const noAvailability = { date: formattedDay, times: [], timezoneAbbr: '' } //** Default value */

  if (availableAppointments && availableAppointments.length > 0) {
    const foundAvailability = availableAppointments.reduce(
      (availability, appointment) => {
        if (appointment.date === formattedDay) {
          return {
            ...availability,
            times: [
              ...availability.times,
              {
                id: appointment.availabilitySlotId,
                appointmentType: appointment.appointmentType.name,
                startTime: appointment.startTime,
                provider: appointment.providerName,
              },
            ],
            timezoneAbbr: appointment.timezoneAbbr,
          }
        }
        return availability
      },
      { date: formattedDay, times: [], timezoneAbbr: '' }
    )
    // Sort the times array to ensure correct ordering
    foundAvailability.times.sort((a, b) => {
      // Sort by startTime
      if (a.startTime < b.startTime) return -1
      if (a.startTime > b.startTime) return 1
      // If startTimes are equal, sort by provider name
      return a.provider.localeCompare(b.provider)
    })
    // Return availability if found; otherwise, return the default no availability
    return foundAvailability.times.length ? foundAvailability : noAvailability
  }
  return noAvailability
}